import { Flex, Typography, Table, TableProps } from "antd";
import { CalendarSchedule } from "components/WeeklyCalendar/components/types";
import dayjs from "dayjs";
import { ActionedSchedules } from "pages/schedule/component/ActionedSchedules";
import {
  ScheduleStatusBadge,
  ClassTypeBadge,
} from "pages/schedule/component/ScheduleStatusBadge";
import { InstructorAvatar } from "pages/schedule/component/InstructorAvatar";
import { getAllClassTypeByBusinessId } from "hooks/queries/classtype";
import { ClassCategory } from "shared/types/enum";
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import PATHS from "shared/constants/paths";
import { translate } from "shared/languages";
import { colors } from "shared/theme/color";
import { formatDateNoTimeToLocalString } from "shared/utils";
import { tzDate, now } from "shared/utils/dayjs";
import { textPx } from "shared/constants/zoom";
import { CapacityCell } from "./CapacityCell";
import { ClientNamesCell } from "./ClientNamesCell";

// Minimalist uppercase ink-3 column header (prototype `table.tbl th`).
const thStyle: React.CSSProperties = {
  textTransform: "uppercase",
  fontSize: textPx(11),
  fontWeight: 600,
  letterSpacing: "0.06em",
  color: colors.ink3,
};
const th = (label: string) => <span style={thStyle}>{label}</span>;
const tnum: React.CSSProperties = { fontVariantNumeric: "tabular-nums" };
const ellipsis: React.CSSProperties = {
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
  minWidth: 0,
};

// Marks the Klien header cell so its width can be read (every cell of a
// fixed-layout column is that wide).
const CLIENTS_COL_CLASS = "schedule-list-clients-col";

type ScheduleTableProps = {
  date: string;
  schedules: CalendarSchedule[];
  // Full names of the clients the list is filtered on: shown first and
  // highlighted in the Klien cell.
  highlightNames: Set<string>;
};

export const ScheduleTable: React.FC<ScheduleTableProps> = ({
  date,
  schedules,
  highlightNames,
}) => {
  const navigate = useNavigate();
  const localDate = dayjs.tz(date);
  const isToday = now().isSame(localDate, "date");
  const isFuture = isToday || now().isBefore(localDate, "date");
  const displayedDate = isToday
    ? translate("schedule.feature.today")
    : formatDateNoTimeToLocalString(localDate);

  const sortedSchedules = useMemo(
    () =>
      [...schedules].sort(
        (a, b) =>
          new Date(a.start_time).getTime() - new Date(b.start_time).getTime()
      ),
    [schedules]
  );

  const pendingSchedules = useMemo(
    () => schedules.filter((s) => s.need_action),
    [schedules]
  );

  // The merged list mixes Open + Private; map class_id -> type so each row can
  // show a Jenis Kelas badge. (react-query dedupes this across day groups.)
  const { data: classes } = getAllClassTypeByBusinessId(undefined);
  const classTypeById = useMemo(
    () => new Map((classes ?? []).map((c) => [c.id, c.type as ClassCategory])),
    [classes]
  );

  // The Klien column's content width, for fitting names on one line. Layout
  // px (clientWidth), never a bounding rect, inside the body zoom.
  const hasSchedules = schedules.length > 0;
  const wrapRef = useRef<HTMLDivElement | null>(null);
  const [clientsWidth, setClientsWidth] = useState(0);
  useLayoutEffect(() => {
    const wrap = wrapRef.current;
    if (!wrap) return;
    const measureColumn = () => {
      const th = wrap.querySelector<HTMLElement>(`th.${CLIENTS_COL_CLASS}`);
      if (!th) return;
      const style = getComputedStyle(th);
      setClientsWidth(
        Math.floor(
          th.clientWidth -
            parseFloat(style.paddingLeft) -
            parseFloat(style.paddingRight)
        )
      );
    };
    measureColumn();
    const observer = new ResizeObserver(measureColumn);
    observer.observe(wrap);
    return () => observer.disconnect();
  }, [hasSchedules]);

  // Names are measured in the web font, so fit them again once it has loaded.
  const [, setFontsLoaded] = useState(false);
  useEffect(() => {
    let alive = true;
    document.fonts?.ready
      .then(() => alive && setFontsLoaded(true))
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, []);

  const onEventClick = (event: CalendarSchedule) => {
    navigate(
      `${PATHS.SCHEDULE}/${
        event.schedule_id
      }?occurrence_timestamp=${encodeURIComponent(event.start_time.toString())}`
    );
  };

  const typeOf = (record: CalendarSchedule) =>
    (record.class_category as ClassCategory | undefined) ??
    (record.class_id ? classTypeById.get(record.class_id) : undefined);

  // Fixed widths for the short columns, shares for the middle ones and the
  // rest for Klien, so a long client list never squeezes the others and a
  // narrow window still leaves Klien room for a name and its "+N".
  const ScheduleTableColumns: TableProps<CalendarSchedule>["columns"] = [
    {
      title: th(translate("label.time")),
      key: "time",
      width: 120,
      render: (_, record) => {
        const start = tzDate(record.start_time).format("HH:mm");
        const end = tzDate(record.end_time).format("HH:mm");
        return <span style={tnum}>{`${start} - ${end}`}</span>;
      },
    },
    {
      title: th(translate("class")),
      dataIndex: "class_name",
      key: "class_name",
      width: "16%",
      ellipsis: true,
      render: (name) => name || "-",
    },
    {
      title: th(translate("schedule.class_type")),
      key: "class_type",
      width: 112,
      render: (_, record) => <ClassTypeBadge type={typeOf(record)} />,
    },
    {
      title: th(translate("instructor")),
      dataIndex: "instructor_name",
      key: "instructor_name",
      width: "12%",
      render: (name: string | undefined, record) => (
        <Flex align="center" gap={10} style={{ minWidth: 0 }}>
          <InstructorAvatar
            instructorId={record.instructor_id ?? record.staff_id}
            name={name}
          />
          <span style={ellipsis} title={name}>
            {name || "-"}
          </span>
        </Flex>
      ),
    },
    {
      title: th(translate("schedule.list_table.capacity")),
      key: "capacity",
      width: "15%",
      render: (_, record) => (
        <CapacityCell schedule={record} type={typeOf(record)} />
      ),
    },
    {
      title: th(translate("customer")),
      dataIndex: "customer_names",
      key: "customer_names",
      onHeaderCell: () => ({ className: CLIENTS_COL_CLASS }),
      render: (_, record) => (
        <ClientNamesCell
          schedule={record}
          width={clientsWidth}
          highlight={highlightNames}
          onOpenDetail={() => onEventClick(record)}
        />
      ),
    },
    {
      title: th("Status"),
      key: "status",
      width: 120,
      render: (_, record) => <ScheduleStatusBadge schedule={record} />,
    },
  ];

  return (
    <Flex
      vertical
      gap={8}
      style={{
        padding: 20,
        border: `1px solid ${colors.line}`,
        borderRadius: 16,
        background: colors.card,
      }}
    >
      <Flex
        gap={10}
        align="center"
        justify={"space-between"}
        style={{ paddingBottom: 6 }}
      >
        <Flex gap={11} align="center">
          <div
            style={{
              width: 3,
              height: 20,
              backgroundColor: isFuture ? colors.navy : colors.ink3,
              borderRadius: 999,
            }}
          />
          <Typography.Title
            level={5}
            style={{ margin: 0, fontFamily: "var(--font-display)" }}
          >
            {displayedDate}
          </Typography.Title>
          {schedules.length > 0 && (
            <Typography.Text
              style={{
                color: colors.ink3,
                fontWeight: 500,
                fontSize: textPx(12.5),
              }}
            >
              · {schedules.length} {translate("schedule.jadwal_count")}
            </Typography.Text>
          )}
        </Flex>
        <ActionedSchedules date={displayedDate} schedules={pendingSchedules} />
      </Flex>
      {hasSchedules && (
        <div ref={wrapRef}>
          <Table<CalendarSchedule>
            sticky
            size="small"
            tableLayout="fixed"
            rowKey={(record) =>
              `${record.schedule_id}-${
                record.occurrence_timestamp ?? record.start_time
              }`
            }
            columns={ScheduleTableColumns}
            dataSource={sortedSchedules}
            pagination={false}
            onRow={(record) => {
              return {
                onClick: () => onEventClick(record),
                style: { cursor: "pointer" },
              };
            }}
          />
        </div>
      )}
    </Flex>
  );
};
